// Soundboard service using Web Audio API for 100% offline, latency-free SFX & Jingles
export interface SoundEffectItem {
  id: string;
  name: string;
  emoji: string;
  shortcut: string;
  category: 'music' | 'reaction' | 'transition';
  durationSec: number;
}

export const SOUNDBOARD_PRESETS: SoundEffectItem[] = [
  { id: 'intro_jingle', name: 'Intro Theme', emoji: '🎵', shortcut: '1', category: 'music', durationSec: 3.2 },
  { id: 'applause', name: 'Applause & Cheers', emoji: '👏', shortcut: '2', category: 'reaction', durationSec: 2.8 },
  { id: 'laughter', name: 'Laugh Track', emoji: '😂', shortcut: '3', category: 'reaction', durationSec: 2.2 },
  { id: 'whoosh', name: 'Whoosh Transition', emoji: '💨', shortcut: '4', category: 'transition', durationSec: 1.1 },
  { id: 'airhorn', name: 'Air Horn (Hype)', emoji: '🎺', shortcut: '5', category: 'reaction', durationSec: 1.4 },
  { id: 'ding', name: 'Correct / Ding', emoji: '🔔', shortcut: '6', category: 'reaction', durationSec: 1.2 },
  { id: 'rimshot', name: 'Rimshot / Joke', emoji: '🥁', shortcut: '7', category: 'reaction', durationSec: 1.5 },
  { id: 'outro_music', name: 'Outro Chill Chords', emoji: '🎶', shortcut: '8', category: 'music', durationSec: 3.8 },
];

class SoundboardService {
  private audioCtx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;
  private volume: number = 0.8;

  private initContext() {
    if (!this.audioCtx) {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new AudioCtxClass();
      this.masterGain = this.audioCtx.createGain();
      this.masterGain.gain.setValueAtTime(this.volume, this.audioCtx.currentTime);
      this.masterGain.connect(this.audioCtx.destination);

      try {
        this.destinationNode = this.audioCtx.createMediaStreamDestination();
        this.masterGain.connect(this.destinationNode);
      } catch (e) {
        console.warn('Could not create destination stream for soundboard:', e);
      }
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
  }

  public setVolume(vol: number) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.masterGain && this.audioCtx) {
      this.masterGain.gain.setTargetAtTime(this.volume, this.audioCtx.currentTime, 0.05);
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  public getStreamTrack(): MediaStreamTrack | null {
    this.initContext();
    return this.destinationNode?.stream?.getAudioTracks()[0] || null;
  }

  public playSound(id: string) {
    this.initContext();
    if (!this.audioCtx || !this.masterGain) return;

    const ctx = this.audioCtx;
    const now = ctx.currentTime;

    switch (id) {
      case 'intro_jingle':
        this.synthesizeIntro(ctx, now);
        break;
      case 'applause':
        this.synthesizeApplause(ctx, now);
        break;
      case 'laughter':
        this.synthesizeLaughter(ctx, now);
        break;
      case 'whoosh':
        this.synthesizeWhoosh(ctx, now);
        break;
      case 'airhorn':
        this.synthesizeAirhorn(ctx, now);
        break;
      case 'ding':
        this.synthesizeDing(ctx, now);
        break;
      case 'rimshot':
        this.synthesizeRimshot(ctx, now);
        break;
      case 'outro_music':
        this.synthesizeOutro(ctx, now);
        break;
      default:
        this.synthesizeDing(ctx, now);
        break;
    }
  }

  // 1. Intro Jingle: Upbeat bright modern chord progression
  private synthesizeIntro(ctx: AudioContext, start: number) {
    const chords = [
      [261.63, 329.63, 392.00, 523.25], // C Major
      [293.66, 369.99, 440.00, 587.33], // D Major
      [329.63, 392.00, 493.88, 659.25], // E Minor
      [349.23, 440.00, 523.25, 698.46]  // F Major / Sparkle
    ];

    chords.forEach((chord, chordIdx) => {
      const chordStart = start + chordIdx * 0.5;
      chord.forEach((freq, noteIdx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = noteIdx === 0 ? 'triangle' : 'sine';
        osc.frequency.setValueAtTime(freq, chordStart);

        gain.gain.setValueAtTime(0, chordStart);
        gain.gain.linearRampToValueAtTime(0.18 / (noteIdx + 1), chordStart + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, chordStart + 0.65);

        osc.connect(gain);
        gain.connect(this.masterGain!);

        osc.start(chordStart);
        osc.stop(chordStart + 0.7);
      });
    });

    // Final sparkle chime at 2.1s
    const chime = ctx.createOscillator();
    const chimeGain = ctx.createGain();
    chime.type = 'sine';
    chime.frequency.setValueAtTime(1046.5, start + 2.0); // C6
    chimeGain.gain.setValueAtTime(0.2, start + 2.0);
    chimeGain.gain.exponentialRampToValueAtTime(0.0001, start + 3.2);
    chime.connect(chimeGain);
    chimeGain.connect(this.masterGain!);
    chime.start(start + 2.0);
    chime.stop(start + 3.2);
  }

  // 2. Applause: Bandpassed randomized bursts
  private synthesizeApplause(ctx: AudioContext, start: number) {
    const duration = 2.8;
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      // Pinkish noise with rhythmic clapping modulation
      const clapPulse = Math.sin(i / 1500) * 0.3 + Math.sin(i / 3200) * 0.4 + 0.5;
      data[i] = (Math.random() * 2 - 1) * clapPulse;
    }

    const noiseNode = ctx.createBufferSource();
    noiseNode.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1200, start);
    filter.Q.setValueAtTime(1.2, start);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(0.35, start + 0.3);
    gain.gain.setValueAtTime(0.35, start + 2.0);
    gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

    noiseNode.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    noiseNode.start(start);
    noiseNode.stop(start + duration);
  }

  // 3. Laughter / Chuckle: Modulated playful tone bursts
  private synthesizeLaughter(ctx: AudioContext, start: number) {
    const chuckleCount = 5;
    for (let i = 0; i < chuckleCount; i++) {
      const cStart = start + i * 0.22;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sawtooth';
      const baseFreq = 260 + (i % 2 === 0 ? 40 : -20) + Math.random() * 30;
      osc.frequency.setValueAtTime(baseFreq, cStart);
      osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.7, cStart + 0.18);

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(1400, cStart);

      gain.gain.setValueAtTime(0, cStart);
      gain.gain.linearRampToValueAtTime(0.25, cStart + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, cStart + 0.2);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.masterGain!);

      osc.start(cStart);
      osc.stop(cStart + 0.22);
    }
  }

  // 4. Whoosh / Swish: Low-pass resonant sweep
  private synthesizeWhoosh(ctx: AudioContext, start: number) {
    const duration = 1.1;
    const bufferSize = Math.round(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.setValueAtTime(3.0, start);
    filter.frequency.setValueAtTime(150, start);
    filter.frequency.exponentialRampToValueAtTime(2400, start + 0.5);
    filter.frequency.exponentialRampToValueAtTime(200, start + duration);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, start);
    gain.gain.linearRampToValueAtTime(0.4, start + 0.45);
    gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    noise.start(start);
    noise.stop(start + duration);
  }

  // 5. Airhorn: Dual square wave blast
  private synthesizeAirhorn(ctx: AudioContext, start: number) {
    const tones = [
      { start: 0, dur: 0.18 },
      { start: 0.22, dur: 0.18 },
      { start: 0.44, dur: 0.45 }
    ];

    tones.forEach(t => {
      const bStart = start + t.start;
      [466.16, 587.33].forEach(freq => { // Bb4 + D5
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, bStart);
        osc.frequency.linearRampToValueAtTime(freq * 1.02, bStart + t.dur);

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(2200, bStart);

        gain.gain.setValueAtTime(0.25, bStart);
        gain.gain.exponentialRampToValueAtTime(0.001, bStart + t.dur);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain!);

        osc.start(bStart);
        osc.stop(bStart + t.dur);
      });
    });
  }

  // 6. Ding / Bell
  private synthesizeDing(ctx: AudioContext, start: number) {
    const osc = ctx.createOscillator();
    const oscHarmonic = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(1318.5, start); // E6
    oscHarmonic.type = 'sine';
    oscHarmonic.frequency.setValueAtTime(2637.0, start); // E7

    gain.gain.setValueAtTime(0.35, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 1.2);

    osc.connect(gain);
    oscHarmonic.connect(gain);
    gain.connect(this.masterGain!);

    osc.start(start);
    oscHarmonic.start(start);
    osc.stop(start + 1.2);
    oscHarmonic.stop(start + 1.2);
  }

  // 7. Rimshot: Snappy punch
  private synthesizeRimshot(ctx: AudioContext, start: number) {
    // Drum knock
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(320, start);
    osc.frequency.exponentialRampToValueAtTime(80, start + 0.1);
    oscGain.gain.setValueAtTime(0.5, start);
    oscGain.gain.exponentialRampToValueAtTime(0.001, start + 0.12);
    osc.connect(oscGain);
    oscGain.connect(this.masterGain!);
    osc.start(start);
    osc.stop(start + 0.12);

    // Rim click snap at +0.22s
    const snapStart = start + 0.25;
    const click = ctx.createOscillator();
    const clickGain = ctx.createGain();
    click.type = 'square';
    click.frequency.setValueAtTime(1760, snapStart);
    clickGain.gain.setValueAtTime(0.4, snapStart);
    clickGain.gain.exponentialRampToValueAtTime(0.001, snapStart + 0.08);
    click.connect(clickGain);
    clickGain.connect(this.masterGain!);
    click.start(snapStart);
    click.stop(snapStart + 0.09);

    // Cymbal crash fizz at +0.35s
    const cymbalStart = start + 0.35;
    const dur = 1.0;
    const bufSize = Math.round(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;
    const n = ctx.createBufferSource();
    n.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = 'highpass';
    f.frequency.setValueAtTime(5000, cymbalStart);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.3, cymbalStart);
    g.gain.exponentialRampToValueAtTime(0.0001, cymbalStart + dur);
    n.connect(f);
    f.connect(g);
    g.connect(this.masterGain!);
    n.start(cymbalStart);
    n.stop(cymbalStart + dur);
  }

  // 8. Outro: Warm jazz chill progression
  private synthesizeOutro(ctx: AudioContext, start: number) {
    const chords = [
      [349.23, 440.00, 523.25, 659.25], // Fmaj7
      [329.63, 392.00, 493.88, 587.33], // Em7
      [293.66, 349.23, 440.00, 523.25], // Dm7
      [261.63, 329.63, 392.00, 523.25]  // Cmaj7
    ];

    chords.forEach((chord, i) => {
      const cTime = start + i * 0.75;
      chord.forEach((freq, noteIdx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, cTime);

        gain.gain.setValueAtTime(0, cTime);
        gain.gain.linearRampToValueAtTime(0.14 / (noteIdx + 1), cTime + 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, cTime + 1.1);

        osc.connect(gain);
        gain.connect(this.masterGain!);

        osc.start(cTime);
        osc.stop(cTime + 1.15);
      });
    });
  }
}

export const soundboardService = new SoundboardService();
